import React, { Dispatch, useEffect, useState } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import ViewMessage from '@components/ViewMessage';
import { NexusFlowerIcon } from '@components/Icons/AIFlower';
import { startConversation } from '@actions/nexus';
import styles from './ConversationStarterSuggestions.module.scss';
import Contact from '@models/Contact';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import birthdayIcon from '/img/icons/birthday.svg';
import reconnectIcon from '/img/feed/reconnect-icon.svg';
import Badge from '@components/Contact/Badges/Badge';
import { ID } from '@app/types';
import { useAuthContext } from '@hooks/use-auth-context';
import { lastValueFrom } from 'rxjs';
import { fetchSearchResultsObservable } from '@actions/search';
import { ArrowRightIcon } from '@components/Icons';
import { cacheContacts } from '@actions/ui';
import { ContactCardsModule, Suggestion } from './ContactCard/ContactCard';
import { selectNexusStatus } from '@reducers/data/nexus';
import { useContactLimitExceeded } from '@support/hooks/use-contact-limit-exceeded';

const BirthdayIdeas = (props: {
  contacts: Contact[];
  setTargetContactID?: Dispatch<ID>;
  onFallbackClick: () => void;
}) => {
  return (
    <ContactCardsModule
      contacts={props.contacts.slice(-5)}
      contactToPrompt={(contact: Contact) =>
        `I want to get a gift for my friend ${contact?.displayName}. Can you suggest some gifts they might like?`
      }
      detailType="birthday"
      renderPrompt={(prompt) => (
        <>
          {prompt} <Badge src={birthdayIcon} />
        </>
      )}
      setTargetContactID={props.setTargetContactID}
      shouldSetPrompt
      fallback="Asking for gift ideas for friends is a great place to start! We didn't find any upcoming birthdays in your network - check out some other ideas."
      onFallbackClick={props.onFallbackClick}
    />
  );
};

const EmailOutreach = (props: { contacts: Contact[]; setTargetContactID?: Dispatch<ID> }) => (
  <ContactCardsModule
    contacts={props.contacts.slice(-5)}
    contactToPrompt={(contact: Contact) =>
      `Draft an email to ${contact?.displayName} asking if they would like to catch up soon`
    }
    detailType="lastInteraction"
    renderPrompt={(prompt) => (
      <>
        {prompt} <Badge src={reconnectIcon} />
      </>
    )}
    setTargetContactID={props.setTargetContactID}
    shouldSetPrompt
  />
);

const genericSuggestions = [
  'Who do I know who speaks French?',
  'What non-profits in my network can I donate my time to?',
  "Who do I know who's interested in cooking?",
  "I'm hosting an event for fintech founders in SF. Who should I invite?",
];

const StarterQuestions = ({
  suggestions,
  className,
  targetContactID,
}: {
  suggestions: string[];
  className?: string;
  targetContactID?: ID;
}) => {
  const dispatch = useDispatch();

  return (
    <ul className={cn(styles.suggestionsList, className)}>
      {suggestions.map((suggestion, i) => (
        <li
          key={i}
          className={styles.suggestion}
          onClick={() => dispatch(startConversation(suggestion, targetContactID))}
        >
          <Suggestion>{suggestion}</Suggestion>
        </li>
      ))}
    </ul>
  );
};

type ModuleState = 'birthday' | 'email' | 'starters';
const moduleOrder: ModuleState[] = ['starters', 'email', 'birthday'];

function MoreExamplesBtn<T>({
  order,
  state,
  setState,
}: {
  order: T[];
  state: T;
  setState: Dispatch<T>;
}) {
  return (
    <button
      className={styles.moreIdeasBtn}
      onClick={() => {
        let nextIdx = order.indexOf(state) + 1;
        if (nextIdx >= order.length) {
          nextIdx = 0;
        }
        setState(order[nextIdx]);
      }}
    >
      More Examples <ArrowRightIcon />
    </button>
  );
}

const GenericStarters = ({ setTargetContactID }: { setTargetContactID?: Dispatch<ID> }) => {
  const { user } = useAuthContext();
  const dispatch = useDispatch();
  const contactLimitExceeded = useContactLimitExceeded();
  const [moduleState, setModuleState] = useState<ModuleState>(moduleOrder[0]);
  const [upcomingBirthdayContacts, setUpcomingBirthdayContacts] = useState([]);
  const [emailOutreachContacts, setEmailOutreachContacts] = useState([]);

  useEffect(() => {
    const fetchContactsForSearchTerm = async (searchTerm: string) => {
      return await lastValueFrom(
        fetchSearchResultsObservable(user, searchTerm, 10, contactLimitExceeded),
      );
    };

    fetchContactsForSearchTerm('upcoming birthdays').then(({ response }) => {
      const results = response.hits.hits?.map((hit: any) => hit._source);

      if (!!results) {
        const contacts = results.map((result) => new Contact(result));
        setUpcomingBirthdayContacts(contacts);
        dispatch(cacheContacts(contacts));
      }
    });

    fetchContactsForSearchTerm('a while ago').then(({ response }) => {
      const results = response.hits.hits?.map((hit: any) => hit._source);

      if (!!results) {
        const contacts = results.map((result) => new Contact(result));
        setEmailOutreachContacts(contacts);
        dispatch(cacheContacts(contacts));
      }
    });
  }, []);

  return (
    <ViewMessage
      className={styles.emptyConversationMessage}
      icon={
        <div className={styles.flowerContainer}>
          <NexusFlowerIcon />
        </div>
      }
      headline={<span>What can I ask Nexus?</span>}
      underHeader
      message={
        <div>
          Anything really. Here are some ideas!
          <br />
          <MoreExamplesBtn order={moduleOrder} state={moduleState} setState={setModuleState} />
          <StateBasedFadeTransition
            state={moduleState}
            elementByStateMap={{
              birthday: (
                <BirthdayIdeas
                  contacts={upcomingBirthdayContacts}
                  setTargetContactID={setTargetContactID}
                  onFallbackClick={() => {
                    setModuleState(moduleOrder[0]);
                  }}
                />
              ),
              email: (
                <EmailOutreach
                  contacts={emailOutreachContacts}
                  setTargetContactID={setTargetContactID}
                />
              ),
              starters: <StarterQuestions suggestions={genericSuggestions} />,
            }}
            duration="medium"
          />
        </div>
      }
    />
  );
};

type PanelState = 'generic' | 'nexusProcessing';

export default function ConversationStarterSuggestions({
  setTargetContactID,
}: {
  setTargetContactID?: Dispatch<ID>;
}) {
  const { user } = useAuthContext();
  const nexusStatus = useSelector(selectNexusStatus);
  const accountProcessingMsg = `We'll email you at ${user?.email} when Nexus is ready!`;
  const [panelState, setPanelState] = useState<PanelState>('generic');

  useEffect(() => {
    if (nexusStatus.status === 'processing') {
      setTargetContactID(undefined);
      setTimeout(() => setPanelState('nexusProcessing'), 0);
    }
  }, [nexusStatus]);

  return (
    <div className={styles.startingSuggestions}>
      <div className={styles.gradientBG} />
      <StateBasedFadeTransition
        state={panelState}
        elementByStateMap={{
          generic: (
            <span>
              <GenericStarters setTargetContactID={setTargetContactID} />
            </span>
          ),
          nexusProcessing: (
            <ViewMessage
              className={styles.emptyConversationMessage}
              icon={
                <div className={styles.flowerContainer}>
                  <NexusFlowerIcon />
                </div>
              }
              headline="We're preparing Nexus for you"
              message={accountProcessingMsg}
              underHeader
            />
          ),
        }}
      />
    </div>
  );
}
